import { useState } from "react";
import { motion } from "framer-motion";
import { Search, PhoneCall, AlertTriangle, Shield, Flame, HeartPulse, Building2, Zap, Phone } from "lucide-react";

export default function Emergency() {
  const [search, setSearch] = useState("");

  // അനിമേഷൻ വേരിയന്റുകൾ
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  // 🔥 അത്യാവശ്യ നമ്പറുകളുടെ ഡാറ്റാബേസ് 🔥
  const emergencyData = [
    // --- POLICE STATIONS ---
    { id: 1, name: "Pazhayangadi Police Station", category: "Police", number: "0497-2870233", icon: Shield, color: "bg-blue-100 text-blue-700" },
    { id: 2, name: "Kannapuram Police Station", category: "Police", number: "0497-2860240", icon: Shield, color: "bg-blue-100 text-blue-700" },
    { id: 3, name: "Police Helpline (Toll Free)", category: "Police", number: "112", icon: Shield, color: "bg-blue-100 text-blue-700" },
    { id: 4, name: "Women's Helpline", category: "Police", number: "1091", icon: Shield, color: "bg-pink-100 text-pink-700" },

    // --- FIRE & RESCUE ---
    { id: 5, name: "Fire & Rescue Station, Payyanur", category: "Fire", number: "04985-203201", icon: Flame, color: "bg-red-100 text-red-700" },
    { id: 6, name: "Fire & Rescue Station, Taliparamba", category: "Fire", number: "0460-2200101", icon: Flame, color: "bg-red-100 text-red-700" },
    { id: 7, name: "Fire Emergency (Toll Free)", category: "Fire", number: "101", icon: Flame, color: "bg-red-100 text-red-700" },

    // --- AMBULANCE & MEDICAL ---
    { id: 8, name: "Ambulance Helpline", category: "Medical", number: "108", icon: HeartPulse, color: "bg-green-100 text-green-700" },
    { id: 9, name: "Govt. Taluk Hospital, Pazhayangadi", category: "Medical", number: "0497-2870100", icon: HeartPulse, color: "bg-green-100 text-green-700" },
    
    // --- KSEB (ELECTRICITY) ---
    { id: 10, name: "KSEB Section, Pazhayangadi", category: "Electricity", number: "0497-2870240", icon: Zap, color: "bg-yellow-100 text-yellow-700" },
    { id: 11, name: "KSEB Section, Cherukunnu", category: "Electricity", number: "0497-2860255", icon: Zap, color: "bg-yellow-100 text-yellow-700" },
    { id: 12, name: "KSEB Section, Cheruthazham", category: "Electricity", number: "0497-2801600", icon: Zap, color: "bg-yellow-100 text-yellow-700" },

    // --- PANCHAYATH OFFICES (10 പഞ്ചായത്തുകൾ) ---
    { id: 13, name: "Kalliasseri Panchayath Office", category: "Panchayath", number: "0497-2782755", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 14, name: "Kannapuram Panchayath Office", category: "Panchayath", number: "0497-2860228", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 15, name: "Cherukunnu Panchayath Office", category: "Panchayath", number: "0497-2860233", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 16, name: "Madayi Panchayath Office", category: "Panchayath", number: "0497-2870230", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 17, name: "Mattool Panchayath Office", category: "Panchayath", number: "0497-2843530", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 18, name: "Ezhome Panchayath Office", category: "Panchayath", number: "0497-2870243", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 19, name: "Cheruthazham Panchayath Office", category: "Panchayath", number: "0497-2800366", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 20, name: "Kunhimangalam Panchayath Office", category: "Panchayath", number: "0497-2800455", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 21, name: "Kadannappalli-Panapuzha Panchayath", category: "Panchayath", number: "04985-240030", icon: Building2, color: "bg-gray-100 text-gray-700" },
    { id: 22, name: "Pattuvam Panchayath Office", category: "Panchayath", number: "0460-2244243", icon: Building2, color: "bg-gray-100 text-gray-700" }
  ];

  // സെർച്ച് ഫിൽറ്റർ
  const filteredNumbers = emergencyData.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase()) ||
    item.category.toLowerCase().includes(search.toLowerCase()) ||
    item.number.includes(search)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 pb-24 selection:bg-red-200">
      
      <div className="max-w-5xl mx-auto px-6 pt-8 md:pt-12">
        
        {/* TITLE SECTION */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-4 bg-red-600 text-white rounded-full mb-4 shadow-lg shadow-red-500/30">
            <AlertTriangle size={36} strokeWidth={2.5} className="animate-pulse" />
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
            Emergency <span className="text-red-600">Contacts</span>
          </h2>
          <p className="text-gray-600 mt-3 max-w-lg mx-auto">
            കല്യാശ്ശേരി മണ്ഡലത്തിലെ പോലീസ്, ഫയർഫോഴ്സ്, പഞ്ചായത്ത് ഓഫീസുകൾ തുടങ്ങിയ അത്യാവശ്യ ഫോൺ നമ്പറുകൾ.
          </p>
        </div>

        {/* 🔍 SEARCH SECTION */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="relative">
            <Search size={22} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="പോലീസ്, പഞ്ചായത്ത് അല്ലെങ്കിൽ സ്ഥലം തിരയുക..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-14 pr-4 py-4 rounded-3xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-red-400 focus:border-transparent outline-none transition-all text-lg font-medium"
            />
          </div>
        </div>

        {/* ☎️ NUMBERS GRID */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredNumbers.map((item) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.id}
                variants={itemVariants}
                whileHover={{ y: -4, scale: 1.01 }}
                className="bg-white rounded-3xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col justify-between"
              >
                
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-3 rounded-2xl ${item.color}`}>
                    <Icon size={24} />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-1">
                      {item.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 leading-tight">
                      {item.name}
                    </h3>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                  <span className="font-mono text-lg font-bold text-gray-800 tracking-wide">
                    {item.number}
                  </span>
                  <a 
                    href={`tel:${item.number}`} 
                    className="flex items-center gap-2 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white px-4 py-2 rounded-xl transition-colors duration-300 font-bold text-sm"
                  >
                    <PhoneCall size={16} />
                    Call
                  </a>
                </div>

              </motion.div>
            );
          })}
        </motion.div>

        {/* No Results Found */}
        {filteredNumbers.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center p-4 bg-gray-100 rounded-full mb-4">
              <Phone size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-700">നമ്പറുകൾ കണ്ടെത്താനായില്ല</h3>
            <p className="text-gray-500 mt-2">നിങ്ങൾ തിരഞ്ഞ വിവരങ്ങൾ ഡാറ്റാബേസിൽ ഇല്ല.</p>
          </div>
        )}

      </div>
    </div>
  );
}